/**
 * Support Page - Meilisearch Integration
 */

(function() {
  'use strict';

  const config = window.supportPageConfig || {};

  // DOM Elements
  const elements = {
    searchInput: document.querySelector('[data-search-input]'),
    searchClear: document.querySelector('[data-search-clear]'),
    searchResults: document.querySelector('[data-search-results]'),
    resultsList: document.querySelector('[data-results-list]'),
    resultsEmpty: document.querySelector('[data-results-empty]'),
    resultsLoading: document.querySelector('[data-results-loading]'),
    quickLinks: document.querySelector('[data-quick-links]')
  };

  // State
  let searchTimeout = null;
  let meilisearchClient = null;

  /**
   * Initialize Meilisearch client
   */
  function initMeilisearch() {
    if (!config.meilisearchHost || !config.meilisearchApiKey) {
      console.warn('Meilisearch configuration missing. Search disabled.');
      return false;
    }

    meilisearchClient = {
      host: config.meilisearchHost,
      apiKey: config.meilisearchApiKey,
      index: config.meilisearchIndex || 'articles'
    };

    return true;
  }

  /**
   * Perform search request to Meilisearch
   */
  async function search(query) {
    if (!meilisearchClient) return [];

    const url = `${meilisearchClient.host}/indexes/${meilisearchClient.index}/search`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${meilisearchClient.apiKey}`
        },
        body: JSON.stringify({
          q: query,
          limit: 10,
          filter: `locale = "${config.language === 'fr' ? 'fr' : 'en'}" AND status = "published"`,
          attributesToHighlight: ['name', 'textContent'],
          attributesToCrop: ['textContent'],
          cropLength: 30,
          highlightPreTag: '<mark>',
          highlightPostTag: '</mark>'
        })
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }

      const data = await response.json();
      return data.hits || [];
    } catch (error) {
      console.error('Meilisearch error:', error);
      return [];
    }
  }

  /**
   * Render search results
   */
  function renderResults(hits) {
    if (!elements.resultsList) return;

    if (hits.length === 0) {
      elements.resultsList.innerHTML = '';
      showEmpty(true);
      return;
    }

    showEmpty(false);

    const html = hits.map(hit => {
      // Use 'name' field for title, 'text_content' for excerpt (cropped + highlighted by Meilisearch)
      const title = hit._formatted?.name || hit.name || 'Sans titre';
      const excerpt = hit._formatted?.textContent || hit.textContent || '';
      const url = hit.url ? hit.url : '#';

      return `
        <a href="${url}" class="support-page__result-item">
          <div class="support-page__result-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <path d="M14 2v6h6"></path>
              <line x1="16" x2="8" y1="13" y2="13"></line>
              <line x1="16" x2="8" y1="17" y2="17"></line>
              <line x1="10" x2="8" y1="9" y2="9"></line>
            </svg>
          </div>
          <div class="support-page__result-content">
            <h3 class="support-page__result-title">${title}</h3>
            ${excerpt ? `<p class="support-page__result-excerpt">${excerpt}</p>` : ''}
          </div>
        </a>
      `;
    }).join('');

    elements.resultsList.innerHTML = html;
  }

  /**
   * Show/hide loading state
   */
  function showLoading(show) {
    if (elements.resultsLoading) {
      elements.resultsLoading.classList.toggle('is-visible', show);
    }
    if (elements.resultsList && show) {
      elements.resultsList.innerHTML = '';
    }
  }

  /**
   * Show/hide empty state
   */
  function showEmpty(show) {
    if (elements.resultsEmpty) {
      elements.resultsEmpty.classList.toggle('is-visible', show);
    }
  }

  /**
   * Show/hide search results container
   */
  function showResults(show) {
    if (elements.searchResults) {
      elements.searchResults.classList.toggle('is-active', show);
    }
  }

  /**
   * Show/hide quick links
   */
  function showQuickLinks(show) {
    if (elements.quickLinks) {
      elements.quickLinks.classList.toggle('is-hidden', !show);
    }
  }

  /**
   * Show/hide clear button
   */
  function showClearButton(show) {
    if (elements.searchClear) {
      elements.searchClear.classList.toggle('is-visible', show);
    }
  }

  /**
   * Handle search input
   */
  function handleSearchInput(event) {
    const query = event.target.value.trim();

    showClearButton(query.length > 0);

    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // If query is too short, reset to initial state
    if (query.length < 2) {
      showResults(false);
      showQuickLinks(true);
      showLoading(false);
      showEmpty(false);
      return;
    }

    // Show results container and hide quick links
    showResults(true);
    showQuickLinks(false);

    // Show loading state
    showLoading(true);
    showEmpty(false);

    // Debounce search
    searchTimeout = setTimeout(async () => {
      const hits = await search(query);
      showLoading(false);
      renderResults(hits);
    }, 300);
  }

  /**
   * Handle clear button click
   */
  function handleClearClick() {
    if (elements.searchInput) {
      elements.searchInput.value = '';
      elements.searchInput.focus();
    }
    showClearButton(false);
    showResults(false);
    showQuickLinks(true);
    showLoading(false);
    showEmpty(false);
  }

  /**
   * Handle click outside to close results
   */
  function handleClickOutside(event) {
    const isInsideSearch = event.target.closest('.support-page__search');
    if (!isInsideSearch) {
      if (elements.searchInput) {
        elements.searchInput.value = '';
      }
      showClearButton(false);
      showResults(false);
      showQuickLinks(true);
      showLoading(false);
      showEmpty(false);
    }
  }

  /**
   * Initialize event listeners
   */
  function initEventListeners() {
    if (elements.searchInput) {
      elements.searchInput.addEventListener('input', handleSearchInput);
      elements.searchInput.addEventListener('focus', () => {
        if (elements.searchInput.value.trim().length > 0) {
          showResults(true);
          showQuickLinks(false);
        }
      });
    }

    if (elements.searchClear) {
      elements.searchClear.addEventListener('click', handleClearClick);
    }

    document.addEventListener('click', handleClickOutside);
  }

  /**
   * Initialize
   */
  function init() {
    initMeilisearch();
    initEventListeners();
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
